import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_POLLS = 8;      // ~16s max
const POLL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | timeout | error
  const [tx, setTx] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch {
        setStatus("error");
        return;
      }
      if (attempts.current >= MAX_POLLS) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, POLL_MS);
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="payment-success-page">
      <Card className="max-w-md w-full p-8 border-border bg-white text-center">
        {status === "polling" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-primary" />
            <h1 className="font-display text-2xl font-bold">Confirming payment...</h1>
            <p className="text-muted-foreground text-sm mt-2">Please hold while we verify with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-primary" />
            <div className="eyebrow text-muted-foreground">Success</div>
            <h1 className="font-display text-3xl font-bold mt-1">Payment received</h1>
            {tx && (
              <div className="mt-4 py-4 border-y border-border">
                <div className="font-display text-4xl font-bold">
                  ₹{Number(tx.amount).toLocaleString("en-IN")}
                </div>
                <div className="text-xs text-muted-foreground mt-2 font-mono">{tx.session_id.slice(0, 24)}…</div>
              </div>
            )}
            <Button className="mt-6 w-full" onClick={() => navigate("/fees")} data-testid="back-to-fees">
              Back to Fees
            </Button>
          </>
        )}
        {status === "timeout" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h1 className="font-display text-2xl font-bold">Still processing</h1>
            <p className="text-muted-foreground text-sm mt-2">
              This is taking a bit longer than usual. Your fee will update automatically once Stripe confirms.
            </p>
            <Button variant="outline" className="mt-6 w-full" onClick={() => navigate("/fees")}>
              Back to Fees
            </Button>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
            <h1 className="font-display text-2xl font-bold">Payment not confirmed</h1>
            <p className="text-muted-foreground text-sm mt-2">
              We could not verify this payment. If you were charged, it will appear once Stripe confirms.
            </p>
            <Button variant="outline" className="mt-6 w-full" onClick={() => navigate("/fees")}>
              Back to Fees
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
